import { addArmyBook } from "@/data/redux/armySlice";
import { useAppDispatch } from "@/data/redux/store";
import { GameSystems, IArmyData } from "@/data/types";
import { env } from "@/env";
import { useSlugGameSystem } from "@/hooks";
import useIsDarkMode from "@/hooks/framework/useIsDarkMode";
import useNavigate from "@/hooks/framework/useNavigate";
import TrackingService from "@/services/framework/TrackingService";
import AddIcon from "@mui/icons-material/Add";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { Button, Stack } from "@mui/material";
import { useSearchParams } from "next/navigation";

type Props = {
  armyData: IArmyData;
  mdScreen: boolean;
};

const alpha = env.NEXT_PUBLIC_ENV === "alpha";

export default function ArmyInfoActions({ armyData, mdScreen }: Props) {
  const isDarkMode = useIsDarkMode();
  const isQuestHeroes = [GameSystems.aofq, GameSystems.gfsq].includes(armyData.gameSystemId);
  const isQuestAI = [GameSystems.aofqai, GameSystems.gfsqai].includes(armyData.gameSystemId);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const append = searchParams.get("append") === "true";
  const gameSystem = useSlugGameSystem();
  const pdfUrl = (uid: string, gameSystem: number) => `/armyInfo/${uid}/${gameSystem}/preview`;

  const createList = () => {
    dispatch(addArmyBook(armyData));

    navigate((query) => {
      return isQuestHeroes
        ? {
            path: `/quest/${gameSystem.slug}/create-hero/${armyData.uid}`
          }
        : {
            path: `/list/${gameSystem.slug}/settings`,
            query: { ...query }
          };
    });
  };

  const selectList = () => {
    navigate((query) => ({
      path: `/community-lists/${gameSystem.slug}/${armyData.uid}`,
      query
    }));
  };

  const handlePdfClick = () => {
    const url = pdfUrl(armyData.uid, armyData.gameSystemId);
    TrackingService.TrackEvent("/pdf/" + armyData.flavouredUid, armyData.name + " pdf");
    window.open(url, "_blank");
  };

  return (
    <>
      <Stack
        direction="row"
        sx={{
          alignItems: "stretch"
        }}
      >
        <Button id="btnCreateList" variant="contained" sx={{ flex: 1 }} onClick={createList}>
          <AddIcon sx={{ mr: 1 }} />
          {append ? (
            <span>Add Army Book</span>
          ) : isQuestHeroes ? (
            <span>Create Hero</span>
          ) : isQuestAI ? (
            <span>Create New Collection</span>
          ) : (
            <span>Create New List</span>
          )}
        </Button>
      </Stack>
      {!append && !isQuestHeroes && (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "stretch",
            mt: 1
          }}
        >
          <Button
            color={(mdScreen ? (!isDarkMode ? "white" : "primary") : "primary") as any}
            variant="outlined"
            sx={{ flex: 1 }}
            onClick={handlePdfClick}
          >
            <PictureAsPdfOutlinedIcon sx={{ mr: 1 }} />
            <span>PDF</span>
          </Button>
          {!alpha && (
            <Button
              id="btnSelectList"
              color={(mdScreen ? (!isDarkMode ? "white" : "primary") : "primary") as any}
              variant="outlined"
              sx={{ flex: 3.33 }}
              onClick={selectList}
            >
              <FormatListBulletedIcon sx={{ mr: 1 }} />
              <span>Ready-to-Play Lists</span>
            </Button>
          )}
        </Stack>
      )}
    </>
  );
}
